/**
 * DeactivateReleaseDialog — destructive confirm for flipping a released
 * version to ``DEACTIVATED``.
 *
 * Why type-to-confirm (over a plain "Yes, deactivate" button):
 *
 *   * Deactivation immediately blocks every pool still pinned to the
 *     release — creators get a 409 ``RELEASE_DEACTIVATED`` the next time
 *     a runner tries to dispatch. Agentic networks stop working until the
 *     release is reactivated or the pool is moved.
 *   * Once deactivated, the release becomes eligible for permanent delete
 *     after the grace window.
 *
 * The dialog reads the set of impacted pools from the snapshot row itself
 * (``release.pinnedPools``) — the ``/snapshots`` endpoint enriches each
 * release with pinned-pool info so we don't need a second round-trip.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { deactivateNetworkRelease } from '@/services/networkRestoreApi';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';

function confirmPhraseFor(versionLabel) {
  return `deactivate ${versionLabel}`;
}

export default function DeactivateReleaseDialog({
  open,
  networkId,
  networkName,
  releaseEntry,
  onClose,
  onDeactivated,
}) {
  const releaseVersion = Number(releaseEntry?.releaseVersion || 0);
  // Semantic MAJOR.MINOR label (e.g. "1.10"); the confirm phrase + shown text
  // both use it so the user types exactly what they see. Fallback keeps a
  // sensible phrase for a legacy row with no semantic version.
  const versionLabel = displayVersionLabel(releaseEntry) || `release ${releaseVersion}`;
  const expected = confirmPhraseFor(versionLabel);
  const pools = releaseEntry?.pinnedPools || [];

  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setError(null);
    setSubmitting(false);
  }, [open, releaseVersion]);

  const canSubmit = typed.trim() === expected && !submitting && releaseVersion > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await deactivateNetworkRelease(networkId, releaseVersion);
      onDeactivated?.(response);
    } catch (e) {
      setError(e?.message || 'Failed to deactivate release');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="deactivate-release-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 border-b border-gray-200 px-5 py-4">
              <div className="mt-0.5 rounded-full bg-orange-100 p-1.5 text-orange-600">
                <AlertTriangle className="h-4 w-4" aria-hidden />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">
                  Deactivate release {versionLabel}?
                </h3>
                <p className="mt-1 text-xs text-gray-600">
                  This freezes any Agentic network pool pinned to {versionLabel} on{' '}
                  <strong className="font-medium text-gray-800">{networkName}</strong>.
                  Incoming runs will be rejected until you reactivate or cut a new release.
                </p>
              </div>
            </div>

            <div className="space-y-3 px-5 py-4">
              <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-3 text-xs text-orange-800">
                {pools.length === 0 ? (
                  <p>
                    No pools are currently pinned to {versionLabel}. Deactivating now is safe —
                    this release simply becomes unavailable for future pools.
                  </p>
                ) : (
                  <>
                    <p className="font-medium">
                      {pools.length} {pools.length === 1 ? 'pool is' : 'pools are'} pinned to this release:
                    </p>
                    <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto pl-4 text-[11px]">
                      {pools.map((p) => (
                        <li key={p.poolId} className="list-disc">
                          {p.name || p.poolId}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-orange-700">
                      Runs against {pools.length === 1 ? 'this pool' : 'these pools'} will fail with
                      <code className="mx-1 rounded bg-orange-100 px-1 py-0.5 font-mono text-[10px]">
                        RELEASE_DEACTIVATED
                      </code>
                      until you reactivate or migrate the{pools.length === 1 ? ' pool' : 'm'} to another release.
                    </p>
                  </>
                )}
              </div>

              <label className="block">
                <span className="text-xs font-medium text-gray-700">
                  Type <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">{expected}</code> to confirm
                </span>
                <input
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>

              {error && (
                <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Deactivating…' : `Deactivate ${versionLabel}`}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
